/**
 * types/index.ts
 * ------------------------------------------------------------------
 * The complete Acepsle Hub data model.
 *
 * This file is written in full at Phase 0 even though most modules
 * (invoices, quotes, expenses, the comms hub) are built in later
 * phases. Defining the whole model up front means each module phase
 * only *consumes* these types rather than reshaping the foundation.
 *
 * ── The BURS principle ───────────────────────────────────────────
 * Botswana is moving to mandatory electronic VAT invoicing (a
 * Continuous-Transaction-Control model) for VAT-registered businesses.
 * Acepsle is below the BWP 1,000,000 registration threshold today and
 * does NOT charge VAT. But the model is built "VAT-wired, dormant" so
 * the day Acepsle registers, it is a settings toggle — not a rebuild.
 *
 * Four structural choices make that possible:
 *   1. Every chargeable line carries a `vatTreatment` regardless of
 *      whether VAT is currently charged.
 *   2. Every issued document carries a `vatSnapshot` — the VAT state
 *      AT THE MOMENT IT WAS ISSUED. Flipping registration on in a
 *      future year never retroactively VATs documents raised while
 *      unregistered.
 *   3. Issued documents are immutable in their financial substance:
 *      void-or-credit, never hard-delete. Numbers are gapless.
 *   4. Every money document reserves a `burs` slot for the future
 *      validation token / QR / submission status, dormant until the
 *      BURS technical spec is published and we build the connector.
 * ------------------------------------------------------------------
 */

/* ============================================================ */
/* Shared primitives                                            */
/* ============================================================ */

/** ISO-8601 string (e.g. '2026-06-09' or full timestamp). */
export type ISODateString = string;

/** Currency code. BWP only for now; field exists for future-proofing. */
export type CurrencyCode = 'BWP';

/**
 * VAT treatment of a single line. Meaningful only when the company is
 * VAT-registered; while unregistered every line is effectively
 * 'exempt' and contributes zero VAT, but the chosen treatment is
 * still stored so historical intent is preserved.
 *  - standard: charged at the company's standard rate
 *  - zero:     zero-rated supply (e.g. exports) — 0% but reportable
 *  - exempt:   outside VAT entirely
 */
export type VatTreatment = 'standard' | 'zero' | 'exempt';

/**
 * The VAT state captured on a document at the moment it is issued.
 * This is the forward-looking guarantee: documents keep the VAT
 * reality they were born under.
 */
export interface VatSnapshot {
  /** Was the company VAT-registered when this document was issued? */
  registered: boolean;
  /** Standard rate (percent) in force at issue, e.g. 14. */
  rate: number;
  /** Company VAT number at issue, if registered. */
  vatNumber?: string;
}

/**
 * Reserved slot for future BURS e-invoicing integration. Dormant
 * until the BURS technical specification is published and the
 * submission connector is built. Defined now so the collections never
 * need a schema migration to become compliant.
 */
export interface BursMeta {
  /** Has this document been submitted to the BURS system? */
  submitted?: boolean;
  /** BURS-assigned validation / clearance identifier, once issued. */
  validationId?: string;
  /** Encoded QR payload for the validated document, once issued. */
  qrData?: string;
  /** When submission to BURS succeeded. */
  submittedAt?: ISODateString;
  /** Last submission error, if any (for retry/diagnostics). */
  lastError?: string;
}

/* ============================================================ */
/* Company settings (singleton)                                 */
/* ============================================================ */

export interface BankDetails {
  bankName: string;
  branch?: string;
  accountName: string;
  accountNumber: string;
  branchCode?: string;
  swift?: string;
}

/**
 * The single company-settings document. Stored at
 * `settings/company`. Holds the dormant VAT switch.
 */
export interface CompanySettings {
  /** Legal name, e.g. "Acepsle Proprietary Limited". */
  legalName: string;
  /** Trading name shown on documents if different. */
  tradingName?: string;
  /** Company registration number, e.g. BW00009613165. */
  registrationNumber: string;
  /** BURS Tax Identification Number (TIN) — the seller TIN on invoices. */
  tin?: string;

  /* Contact + address */
  email: string;
  phones: string[];
  physicalAddress: string;
  postalAddress?: string;
  website?: string;
  /** Logo stored in Firebase Storage; URL cached here. */
  logoUrl?: string;

  /* ── VAT: wired but dormant ───────────────────────────────── */
  /** Master switch. false today. Flip to true on registration. */
  vatRegistered: boolean;
  /** Standard VAT rate as a percent. Botswana standard is 14. */
  vatRate: number;
  /** VAT registration number, populated on registration. */
  vatNumber?: string;
  /** Date VAT registration takes effect. */
  vatRegistrationDate?: ISODateString;
  /** Free-text note, e.g. exemption-certificate reference while applying. */
  vatStatusNote?: string;

  /* Document defaults */
  currency: CurrencyCode;
  /** Default payment terms in days (e.g. 7, 14, 30). */
  defaultPaymentTermDays: number;
  /** Default footer/notes printed on invoices. */
  invoiceFooter?: string;
  /** Default footer/notes printed on quotes. */
  quoteFooter?: string;
  /** Where clients pay. */
  bank?: BankDetails;

  /* Signatory + company letters */
  /** Name of the director who signs company letters. */
  directorName?: string;
  /** Title shown under the signature, e.g. "Director". */
  directorTitle?: string;
  /** Director's signature as a PNG data URL, applied to letters. */
  directorSignature?: string;

  /** Audit. */
  updatedAt: ISODateString;
  updatedBy: string;
}

/** Convenient default used when bootstrapping a fresh settings doc. */
export const DEFAULT_COMPANY_SETTINGS: CompanySettings = {
  legalName: 'Acepsle Proprietary Limited',
  registrationNumber: 'BW00009613165',
  email: 'info@acepslebotswana.com',
  phones: ['+267 73 453 092', '+267 75 470 389'],
  physicalAddress: 'Plot 37043 Block 8, Gaborone',
  vatRegistered: false,
  vatRate: 14,
  vatStatusNote: 'Below VAT registration threshold; exemption certificate pending.',
  currency: 'BWP',
  defaultPaymentTermDays: 14,
  updatedAt: '',
  updatedBy: '',
};

/* ============================================================ */
/* Users (light auth, expandable)                               */
/* ============================================================ */

/**
 * Roles. 'admin' is the system administrator (the only role that can
 * manage users). 'operator' is kept as a legacy alias for admin.
 */
export type UserRole = 'admin' | 'manager' | 'bookkeeper' | 'sales' | 'viewer' | 'operator';

export interface AppUser {
  /** Firebase Auth uid, set on first sign-in (allowlist is email-keyed). */
  uid?: string;
  email: string;
  displayName?: string;
  role: UserRole;
  active: boolean;
  createdAt: ISODateString;
  createdBy?: string;
  updatedAt?: ISODateString;
}

/* ============================================================ */
/* Clients                                                      */
/* ============================================================ */

export type ClientType = 'school' | 'company' | 'individual' | 'other';

export interface ClientContact {
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  /** Primary contact receives invoice/statement emails by default. */
  primary?: boolean;
}

export interface Client {
  id: string;
  name: string;
  type: ClientType;
  /** Buyer TIN — required on a BURS tax invoice once registered. */
  tin?: string;
  /** Buyer VAT number, if the client is itself VAT-registered. */
  vatNumber?: string;
  registrationNumber?: string;

  email?: string;
  phone?: string;
  contacts: ClientContact[];

  billingAddress?: string;
  notes?: string;

  active: boolean;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  createdBy: string;
}

/**
 * A frozen snapshot of the client written onto a document at issue
 * time. Editing the client later never mutates documents already
 * issued — required for an auditable, gapless record.
 */
export interface ClientSnapshot {
  clientId: string;
  name: string;
  tin?: string;
  vatNumber?: string;
  email?: string;
  billingAddress?: string;
}

/* ============================================================ */
/* Line items (shared by invoices, quotes, credit notes)        */
/* ============================================================ */

export interface LineItem {
  /** Stable id within the document (for React keys / edits). */
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  vatTreatment: VatTreatment;
  /** quantity * unitPrice, rounded to 2dp. */
  lineNet: number;
  /** VAT on this line (0 while unregistered), rounded to 2dp. */
  lineVat: number;
  /** lineNet + lineVat, rounded to 2dp. */
  lineTotal: number;
}

/** Money totals shared by document types. */
export interface DocumentTotals {
  /** Sum of line nets. */
  subtotal: number;
  /** Sum of line VATs (0 while unregistered). */
  vatTotal: number;
  /** subtotal + vatTotal. */
  total: number;
}

/* ============================================================ */
/* Invoices                                                     */
/* ============================================================ */

export type InvoiceStatus =
  | 'draft' // editable, no number assigned yet
  | 'issued' // number assigned, sent / sendable, nothing paid
  | 'part_paid' // some payments recorded, balance outstanding
  | 'paid' // fully settled
  | 'void'; // cancelled after issue (number retained, never reused)

export interface Invoice {
  id: string;
  /** Gapless sequential number, assigned ONLY on issue. Null while draft. */
  number: string | null;
  status: InvoiceStatus;

  clientId: string;
  /** Frozen client details at issue. */
  client: ClientSnapshot;

  issueDate: ISODateString;
  dueDate: ISODateString;

  lineItems: LineItem[];
  totals: DocumentTotals;
  currency: CurrencyCode;

  /** VAT reality captured at issue. */
  vat: VatSnapshot;

  /** Running payment figures (kept in sync as payments are recorded). */
  amountPaid: number;
  balance: number;

  notes?: string;
  terms?: string;

  /** If generated from a recurring template. */
  recurringSourceId?: string;
  /** If this invoice was created by converting a quote. */
  sourceQuoteId?: string;

  /** Reserved for future BURS submission. */
  burs?: BursMeta;

  createdAt: ISODateString;
  updatedAt: ISODateString;
  issuedAt?: ISODateString;
  voidedAt?: ISODateString;
  voidReason?: string;
  createdBy: string;
}

/* ============================================================ */
/* Payments + receipts                                          */
/* ============================================================ */

export type PaymentMethod =
  | 'bank_transfer'
  | 'cash'
  | 'cheque'
  | 'card'
  | 'mobile'
  | 'other';

/**
 * A payment recorded against an invoice. Every payment generates a
 * receipt (its own gapless receipt number) — there is no separate
 * receipt entity; the receipt is the PDF rendering of this record.
 */
export interface Payment {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;

  amount: number;
  date: ISODateString;
  method: PaymentMethod;
  /** Bank/cheque/transaction reference. */
  reference?: string;

  /** Gapless sequential receipt number. */
  receiptNumber: string;

  notes?: string;
  /** Set true if this payment was reversed; reversals never delete. */
  reversed?: boolean;
  reversedAt?: ISODateString;
  reversalReason?: string;

  createdAt: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Quotes                                                       */
/* ============================================================ */

export type QuoteStatus =
  | 'draft'
  | 'sent'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'converted'; // turned into an invoice

export interface Quote {
  id: string;
  /** Gapless sequential number, assigned on first send. */
  number: string | null;
  status: QuoteStatus;

  clientId: string;
  client: ClientSnapshot;

  issueDate: ISODateString;
  validUntil: ISODateString;

  lineItems: LineItem[];
  totals: DocumentTotals;
  currency: CurrencyCode;
  vat: VatSnapshot;

  notes?: string;
  terms?: string;

  /** Set once converted to an invoice. */
  convertedInvoiceId?: string;

  createdAt: ISODateString;
  updatedAt: ISODateString;
  sentAt?: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Credit notes                                                 */
/* ============================================================ */

export type CreditNoteStatus = 'draft' | 'issued' | 'void';

/**
 * The compliant way to reverse or correct an already-issued invoice.
 * Because invoices are never hard-deleted, a credit note documents
 * the reversal with its own gapless number and an audit reason.
 */
export interface CreditNote {
  id: string;
  number: string | null;
  status: CreditNoteStatus;

  /** The invoice being credited. */
  invoiceId: string;
  invoiceNumber: string;

  clientId: string;
  client: ClientSnapshot;

  issueDate: ISODateString;
  reason: string;

  lineItems: LineItem[];
  totals: DocumentTotals;
  currency: CurrencyCode;
  vat: VatSnapshot;

  burs?: BursMeta;

  createdAt: ISODateString;
  updatedAt: ISODateString;
  issuedAt?: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Recurring invoices                                           */
/* ============================================================ */

export type RecurrenceFrequency = 'monthly' | 'quarterly' | 'annual';

/**
 * A template that spawns invoices on a schedule (e.g. the annual
 * maintenance fee). "Generate" produces a real Invoice from the
 * template's line items at run time, capturing a fresh VAT snapshot.
 */
export interface RecurringInvoice {
  id: string;
  active: boolean;

  clientId: string;
  /** Template lines (no per-line ids needed until materialised). */
  lineItems: Array<Omit<LineItem, 'id' | 'lineNet' | 'lineVat' | 'lineTotal'>>;

  frequency: RecurrenceFrequency;
  startDate: ISODateString;
  /** Next date an invoice should be generated. */
  nextRunDate: ISODateString;
  /** Optional end date; null/absent means open-ended. */
  endDate?: ISODateString;

  /** Payment term in days applied to each generated invoice. */
  paymentTermDays: number;

  notes?: string;
  terms?: string;

  lastGeneratedInvoiceId?: string;
  lastGeneratedAt?: ISODateString;

  createdAt: ISODateString;
  updatedAt: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Expenses                                                     */
/* ============================================================ */

export type ExpenseCategory =
  | 'infrastructure' // hosting, cloud, domains
  | 'software' // tools, subscriptions
  | 'professional' // legal, accounting
  | 'marketing'
  | 'travel'
  | 'equipment'
  | 'office'
  | 'bank_charges'
  | 'tax'
  | 'other';

export interface Expense {
  id: string;
  date: ISODateString;
  category: ExpenseCategory;
  /** Who was paid. */
  payee: string;
  description: string;

  /** Net amount (excl. VAT). */
  amount: number;
  /** Input VAT, if any (dormant while unregistered; 0). */
  vatAmount: number;
  /** amount + vatAmount. */
  total: number;

  method: PaymentMethod;
  reference?: string;
  /** Receipt/invoice document in Storage. */
  attachmentUrl?: string;

  notes?: string;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Communications (comms hub — migrated in Phase 1)             */
/* ============================================================ */

export type EmailStatus = 'sent' | 'failed';

/**
 * Audit record of an email sent through the Acepsle comms hub.
 * Mirrors the `acepsleEmailLog` collection migrated from the LKC app.
 */
export interface EmailLogEntry {
  id: string;
  to: string[];
  cc?: string[];
  subject: string;
  /** Body stored for audit (sanitised HTML). */
  body: string;
  /** Template id used, if any. */
  templateId?: string;
  /** Names of attachments included. */
  attachments?: string[];
  /** Linked entity, e.g. an invoice this email delivered. */
  relatedEntityType?: 'invoice' | 'quote' | 'statement' | 'receipt' | 'creditNote';
  relatedEntityId?: string;
  status: EmailStatus;
  error?: string;
  sentAt: ISODateString;
  sentBy: string;
}

/* ============================================================ */
/* Audit trail (cross-cutting)                                  */
/* ============================================================ */

export type AuditEntityType =
  | 'invoice'
  | 'payment'
  | 'quote'
  | 'creditNote'
  | 'recurringInvoice'
  | 'expense'
  | 'client'
  | 'settings'
  | 'siteImage'
  | 'user';

export interface AuditEntry {
  id: string;
  timestamp: ISODateString;
  actorUid: string;
  actorEmail: string;
  /** Verb, e.g. 'invoice.issue', 'payment.record', 'settings.update'. */
  action: string;
  entityType: AuditEntityType;
  entityId: string;
  /** Optional before/after diff for sensitive changes. */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

/* ============================================================ */
/* Numbering counters                                           */
/* ============================================================ */

export type NumberSeries =
  | 'invoice'
  | 'receipt'
  | 'quote'
  | 'creditNote';

/**
 * A monotonic counter for a document series, stored at
 * `counters/{series}-{year}`. Read-incremented inside a transaction
 * to guarantee gapless, collision-free numbers.
 */
export interface Counter {
  series: NumberSeries;
  year: number;
  /** Next sequence value to assign. */
  next: number;
  prefix: string;
  /** Zero-pad width, e.g. 4 -> 0001. */
  padding: number;
}

/* ============================================================ */
/* Firestore collection paths (single source of truth)          */
/* ============================================================ */

/* ============================================================ */
/* Company documents (regulatory / compliance letters)          */
/* ============================================================ */

export type LetterKind =
  | 'account_opening'
  | 'employee_confirmation'
  | 'board_resolution'
  | 'general';

/** An archived company letter generated on letterhead. */
export interface CompanyDocument {
  id: string;
  kind: LetterKind;
  /** Reference shown on the letter, e.g. "ACE-LTR-2026-0007". */
  reference: string;
  /** Letter title / subject line. */
  subject: string;
  /** Addressee block (name + address lines, free text). */
  recipient: string;
  /** Body paragraphs (one string; blank lines separate paragraphs). */
  body: string;
  /** Date on the letter (yyyy-mm-dd). */
  date: ISODateString;
  /** Whether the director's signature image was applied. */
  signed: boolean;
  /** Whether the company stamp was applied. */
  stamped: boolean;
  /** Signatory snapshot frozen at generation. */
  signatoryName?: string;
  signatoryTitle?: string;
  createdAt: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Leads (public enquiries → sales pipeline)                    */
/* ============================================================ */

export type LeadStatus = 'new' | 'contacted' | 'quoted' | 'won' | 'lost';

/** A prospect captured from the public enquiry form. */
export interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  /** Where it came from, e.g. 'website'. */
  source?: string;
  status: LeadStatus;
  /** Internal sales notes. */
  notes?: string;
  /** Set once converted into a client. */
  convertedClientId?: string;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

/* ============================================================ */
/* Compliance register (statutory docs + renewal dates)         */
/* ============================================================ */

export type ComplianceKind =
  | 'tax_clearance'
  | 'incorporation'
  | 'annual_return'
  | 'vat'
  | 'trade_licence'
  | 'other';

/** A statutory/compliance record with an optional uploaded copy. */
export interface ComplianceItem {
  id: string;
  kind: ComplianceKind;
  title: string;
  /** Certificate / reference number (e.g. TCC/26-27/002975, UIN). */
  reference?: string;
  /** Issuing authority, e.g. BURS, CIPA. */
  authority?: string;
  issueDate?: ISODateString;
  /** Renewal / expiry date used for the reminders. */
  expiryDate?: ISODateString;
  /** Uploaded copy in Storage. */
  attachmentUrl?: string;
  attachmentName?: string;
  notes?: string;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  createdBy: string;
}

/* ============================================================ */
/* Site content (images shown on the public company page)       */
/* ============================================================ */

/**
 * One uploaded image, keyed by slot id inside the `siteContent/images`
 * document. Images themselves live in Storage under `site/`; this
 * records where they are and what was produced, so the public page can
 * render them without a build step.
 *
 * NOTE: `siteContent` is world-READABLE by design (see firestore.rules)
 * — the marketing page is public and unauthenticated. Never put
 * anything private in this collection.
 */
export interface SiteImage {
  /** Storage download URL. */
  url: string;
  /** Full path inside the bucket, kept so the file can be replaced/removed. */
  path: string;
  width: number;
  height: number;
  contentType: string;
  bytes: number;
  /** Original filename, shown in the admin list for orientation. */
  originalName: string;
  updatedAt: ISODateString;
  updatedBy: string;
}

/** The `siteContent/images` document: slot id -> image. */
export type SiteImageMap = Record<string, SiteImage>;

export const SITE_IMAGES_DOC_ID = 'images';

export const COLLECTIONS = {
  settings: 'settings',
  siteContent: 'siteContent',
  users: 'users',
  clients: 'clients',
  invoices: 'invoices',
  payments: 'payments',
  quotes: 'quotes',
  creditNotes: 'creditNotes',
  recurringInvoices: 'recurringInvoices',
  expenses: 'expenses',
  companyDocuments: 'companyDocuments',
  leads: 'leads',
  complianceItems: 'complianceItems',
  emailLog: 'acepsleEmailLog',
  audit: 'auditLog',
  counters: 'counters',
} as const;

export const SETTINGS_DOC_ID = 'company';
